import { useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOptimizedQuery } from './useOptimizedQuery';
import { toast } from 'sonner';
import { useRequestMonitor } from './useRequestMonitor';
import { useDebounce } from './useDebounce';
import { logger } from '@/lib/logger';

interface RealtimeSubscriptionPool {
  channels: Map<string, any>;
  subscribers: Map<string, Set<string>>;
}

// Global subscription pool to prevent duplicate connections
const subscriptionPool: RealtimeSubscriptionPool = {
  channels: new Map(),
  subscribers: new Map()
};

interface OptimizedRealtimeQueryOptions<T> {
  queryKey: (string | undefined)[];
  queryFn: () => Promise<T>;
  tableName: string;
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
  refetchInterval?: number;
  debounceDelay?: number;
  forceRefresh?: boolean;
  enableToasts?: boolean;
  priority?: 'high' | 'normal' | 'low';
  refetchOnMount?: boolean | 'always';
  refetchOnWindowFocus?: boolean;
  filter?: string; // e.g. 'project_id=eq.xxx' for row-level filtering
}

export function useOptimizedRealtimeQuery<T>({
  queryKey,
  queryFn,
  tableName,
  enabled = true,
  staleTime,
  gcTime,
  refetchInterval,
  debounceDelay = 800, // Optimized for navigation speed
  forceRefresh,
  enableToasts = false,
  priority = 'normal',
  filter,
  refetchOnMount,
}: OptimizedRealtimeQueryOptions<T>) {
  const queryClient = useQueryClient();
  const { trackRequest } = useRequestMonitor();
  const invalidationCountRef = useRef(0);
  const subscriberIdRef = useRef<string>();

  // Use optimized query as base
  const query = useOptimizedQuery({
    queryKey,
    queryFn,
    enabled,
    staleTime: staleTime || (priority === 'high' ? 20000 : 180000), // 20s for high priority, 3min for others
    gcTime,
    refetchInterval,
    ...(refetchOnMount !== undefined ? { refetchOnMount } : {}),
  });

  // Debounced invalidation to prevent rapid refetches
  const { debouncedCallback: debouncedInvalidate } = useDebounce(() => {
    if (!trackRequest()) return; // Rate limiting
    
    invalidationCountRef.current++;
    queryClient.invalidateQueries({ queryKey });
  }, debounceDelay);

  const invalidateQuery = useCallback(() => {
    debouncedInvalidate();
  }, [debouncedInvalidate]);

  useEffect(() => {
    if (!enabled || priority === 'low') return; // Skip realtime for low priority queries

    // Generate unique subscriber ID
    subscriberIdRef.current = `${tableName}-${Date.now()}-${Math.random()}`;
    const subscriberId = subscriberIdRef.current;
    
    // Pool key includes filter to separate filtered vs unfiltered channels
    const poolKey = `${tableName}:${filter || '*'}`;
    
    // Check if we already have a channel for this table+filter combo
    let channel = subscriptionPool.channels.get(poolKey);
    let subscribers = subscriptionPool.subscribers.get(poolKey) || new Set();

    if (!channel) {
      // Create new pooled channel for this table+filter
      const channelId = `realtime-pool-${poolKey}`;
      
      // Build subscription config with optional filter
      const subscriptionConfig: Record<string, string> = {
        event: '*',
        schema: 'public',
        table: tableName
      };
      if (filter) {
        subscriptionConfig.filter = filter;
      }
      
      channel = supabase
        .channel(channelId)
        .on(
          'postgres_changes',
          subscriptionConfig as any,
          (payload) => {
            logger.debug(`[RealtimePool] Update on ${tableName}:`, payload.eventType);
            
            // Notify all subscribers for this table+filter
            const tableSubscribers = subscriptionPool.subscribers.get(poolKey);
            if (tableSubscribers) {
              // Batch invalidation for all subscribers
              const queryKeys = Array.from(tableSubscribers).map(id => 
                JSON.parse(id) as (string | undefined)[]
              );
              
              queryKeys.forEach(key => {
                setTimeout(() => {
                  queryClient.invalidateQueries({ queryKey: key });
                }, Math.random() * 200); // Reduced stagger for faster updates
              });
            }
            
            if (enableToasts && payload.eventType !== 'UPDATE') { // Reduce toast spam
              switch (payload.eventType) {
                case 'INSERT':
                  if (tableName === 'projects') {
                    toast.success('Có dự án mới được thêm');
                  }
                  break;
                case 'DELETE':
                  if (tableName === 'projects') {
                    toast.warning('Dự án đã được xóa');
                  }
                  break;
              }
            }
          }
        )
        .subscribe((status) => {
          logger.debug(`[RealtimePool] Channel ${tableName} status:`, status);
        });

      subscriptionPool.channels.set(poolKey, channel);
      subscriptionPool.subscribers.set(poolKey, new Set());
    }

    // Add this subscriber to the pool
    subscribers = subscriptionPool.subscribers.get(poolKey)!;
    subscribers.add(JSON.stringify(queryKey));
    subscriptionPool.subscribers.set(poolKey, subscribers);

    logger.debug(`[RealtimePool] Added subscriber to ${poolKey}. Total: ${subscribers.size}`);

    return () => {
      // Remove subscriber from pool
      const currentSubscribers = subscriptionPool.subscribers.get(poolKey);
      if (currentSubscribers) {
        currentSubscribers.delete(JSON.stringify(queryKey));
        
        // If no more subscribers, clean up the channel
        if (currentSubscribers.size === 0) {
          const channel = subscriptionPool.channels.get(poolKey);
          if (channel) {
            logger.debug(`[RealtimePool] Cleaning up channel for ${poolKey}`);
            supabase.removeChannel(channel);
            subscriptionPool.channels.delete(poolKey);
            subscriptionPool.subscribers.delete(poolKey);
          }
        } else {
          logger.debug(`[RealtimePool] Removed subscriber from ${poolKey}. Remaining: ${currentSubscribers.size}`);
        }
      }
    };
  }, [tableName, enabled, queryClient, enableToasts, priority, filter, JSON.stringify(queryKey)]);

  return {
    ...query,
    // Add realtime-specific methods
    realtimeEnabled: enabled && priority !== 'low',
    invalidationCount: invalidationCountRef.current,
    subscriberCount: subscriptionPool.subscribers.get(tableName)?.size || 0,
    optimizedRefetch: () => query.refetch()
  };
}

// Helper hook for non-critical data that doesn't need realtime
export function useStaticQuery<T>(options: Omit<OptimizedRealtimeQueryOptions<T>, 'priority'>) {
  return useOptimizedRealtimeQuery({
    ...options,
    priority: 'low' // This will disable realtime subscriptions
  });
}

// Helper to get current subscription stats (for debugging)
export function useRealtimeStats() {
  return {
    activeChannels: subscriptionPool.channels.size,
    totalSubscribers: Array.from(subscriptionPool.subscribers.values())
      .reduce((sum, set) => sum + set.size, 0),
    channelDetails: Array.from(subscriptionPool.subscribers.entries())
      .map(([table, subscribers]) => ({
        table,
        subscriberCount: subscribers.size
      }))
  };
}